import { Slot } from "../api/DomainModel/Slot";

// Departures happen in Mallorca, so the cutoff must ignore the visitor's device timezone.
export const BOOKING_TIMEZONE = "Europe/Madrid";
export const BOOKING_CUTOFF_MINUTES = 15;

export type BookingNow = {
  dateKey: string;
  minutesOfDay: number;
};

export function getBookingNow(reference: Date = new Date()): BookingNow {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BOOKING_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(reference);

  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  const hour = parseInt(value("hour"), 10) % 24;

  return {
    dateKey: `${value("year")}-${value("month")}-${value("day")}`,
    minutesOfDay: hour * 60 + parseInt(value("minute"), 10),
  };
}

export function parseTimeToMinutes(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time?.trim() ?? "");

  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

// The API sends slot dates as UTC instants, so the departure day only makes sense in Madrid time.
export function getSlotDateKey(slotStartDate: string): string {
  const parsed = new Date(slotStartDate);

  if (isNaN(parsed.getTime())) return "";

  return getBookingNow(parsed).dateKey;
}

export function isTimeBookable(slotStartDate: string, time: string, now: BookingNow = getBookingNow()): boolean {
  const slotDateKey = getSlotDateKey(slotStartDate);

  if (!slotDateKey) return true;
  if (slotDateKey > now.dateKey) return true;
  if (slotDateKey < now.dateKey) return false;

  const minutes = parseTimeToMinutes(time);

  if (minutes === null) return true;

  return minutes >= now.minutesOfDay + BOOKING_CUTOFF_MINUTES;
}

export function withBookableTimes(slot: Slot, now: BookingNow = getBookingNow()): Slot {
  const startDate = slot.slotDate?.startDate ?? "";

  return {
    ...slot,
    departureOriginTimes: (slot.departureOriginTimes ?? []).filter((time) => isTimeBookable(startDate, time, now)),
    departureDestinationTimes: (slot.departureDestinationTimes ?? []).filter((time) =>
      isTimeBookable(startDate, time, now),
    ),
  };
}
